import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import { STATUS_CONFIG, RECORD_TYPE_LABELS } from './constants';
import { useToast } from '../Toast';

/**
 * Custom hook that encapsulates all business logic for RequestDetail.
 * Inputs: request, authorizations, onUpdated, onRefresh, onDeleted
 * Returns: all state values, setters, handlers, and derived values needed by the JSX.
 */
export default function useRequestDetail(request, authorizations, onUpdated, onRefresh, onDeleted) {
  const toast = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [showCreateAuth, setShowCreateAuth] = useState(false);
  const [authStep, setAuthStep] = useState(1); // 1: review, 2: sign
  const [signatureData, setSignatureData] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [justCreated, setJustCreated] = useState(false);
  const [authFlowStatus, setAuthFlowStatus] = useState('idle');
  const [authFlowStep, setAuthFlowStep] = useState(1);
  const [authFlowError, setAuthFlowError] = useState('');
  const [authFlowAuthorizationId, setAuthFlowAuthorizationId] = useState(null);
  const [authFlowSigned, setAuthFlowSigned] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [denialReason, setDenialReason] = useState('');
  const [sendError, setSendError] = useState('');
  const [gmailDraftError, setGmailDraftError] = useState('');
  const [gmailDraftLoading, setGmailDraftLoading] = useState(false);
  const [showSendPreview, setShowSendPreview] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testSendError, setTestSendError] = useState('');
  const [testSendSuccess, setTestSendSuccess] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteError, setNoteError] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [portalLink, setPortalLink] = useState(request.authorization?.shareUrl || '');
  const [portalPin, setPortalPin] = useState('');
  const [portalError, setPortalError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalCopied, setPortalCopied] = useState('');
  const [showDevTestSend, setShowDevTestSend] = useState(false);
  const [showReopenPanel, setShowReopenPanel] = useState(false);
  const [reopenMessage, setReopenMessage] = useState('');
  const [reopenSendFollowUp, setReopenSendFollowUp] = useState(true);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [reopenError, setReopenError] = useState('');

  // Derived values
  const flowStorageKey = `medcaddy:auth-flow:${request.id}`;
  const resolvedAuthorizationId = request.authorizationId || request.authorization?.id || authFlowAuthorizationId || null;
  const disclosingEntityName = request.organization || request.providerName || 'Provider';
  const disclosingEntityOrganization = request.organization || '';
  const disclosingEntityAddress = [request.address, request.city, request.state, request.postalCode].filter(Boolean).join(', ');
  const disclosingEntityPhone = request.phone || '';
  const disclosingEntityFax = request.fax || '';
  const revocationContact = 'support@medcaddy.com';

  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.DRAFT;
  const recordTypeLabels = (request.recordTypes || []).map(type => RECORD_TYPE_LABELS[type] || type).join(', ');
  const canComposeEmail = Boolean(request.email);
  const isManualDelivery = !canComposeEmail;
  const hasPortalLink = Boolean((portalLink || request.authorization?.shareUrl || '').trim());
  const isDev = process.env.NODE_ENV !== 'production';
  const sentAtDate = request.sentAt ? new Date(request.sentAt) : null;
  const daysSinceSent = sentAtDate ? Math.floor((Date.now() - sentAtDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const needsFollowUp = Boolean(
    request.sentAt
    && !['RECEIVED', 'PARTIALLY_RECEIVED', 'DENIED', 'CANCELLED'].includes(request.status)
    && daysSinceSent >= 7
  );
  const terminalStatus = ['RECEIVED', 'PARTIALLY_RECEIVED', 'DENIED', 'CANCELLED'].includes(request.status);
  const receivedLike = ['RECEIVED', 'PARTIALLY_RECEIVED'].includes(request.status);
  const timelineStages = ['Draft', 'Sent', 'Follow-up', 'Received/Denied'];
  const currentTimelineIndex = terminalStatus
    ? 3
    : needsFollowUp
      ? 2
      : request.sentAt
        ? 1
        : 0;
  /** Purpose: Executes next action. Inputs: (. Output: Operation result. */
  const nextAction = (() => {
    if (!request.authorizationId) return 'Sign and attach HIPAA authorization.';
    if (!hasPortalLink) return 'Generate a secure portal upload link before sending email.';
    if (!request.sentAt) {
      return canComposeEmail
        ? 'Preview and send the request email.'
        : 'Download the package, send manually, then confirm sent.';
    }
    if (request.status === 'RECEIVED') return 'Records received. Reopen if additional records are needed.';
    if (receivedLike) return 'Review received records and close the request when complete.';
    if (['DENIED', 'CANCELLED'].includes(request.status)) return 'Document denial details and create a revised request if needed.';
    if (needsFollowUp) return 'Contact the provider and log a follow-up reminder.';
    return 'Monitor status and update as soon as the provider responds.';
  })();

  const activeAuthorizations = authorizations.filter(a => (a.status || '').toLowerCase() === 'active');

  // Handlers

  /** Purpose: Builds default email subject/body for request send modal. Inputs: none. Output: email draft values. */
  const buildEmailDraft = useCallback(() => {
    const requestLabel = request.organization || request.providerName || 'Provider';
    const startDate = request.dateRangeStart ? new Date(request.dateRangeStart).toLocaleDateString() : 'Any';
    const endDate = request.dateRangeEnd ? new Date(request.dateRangeEnd).toLocaleDateString() : 'Present';
    const hasDateRange = Boolean(request.dateRangeStart || request.dateRangeEnd);
    const destinationSummary = [request.destinationProviderName, request.destinationOrganization]
      .filter(Boolean)
      .join(' - ');
    const uploadLink = (portalLink || request.authorization?.shareUrl || '').trim();

    const messageLines = [
      `Hello ${request.providerName || 'Provider Team'},`,
      '',
      'Attached is a medical records request package with signed HIPAA authorization.',
      `Requested records: ${recordTypeLabels || 'Medical records'}`,
      hasDateRange ? `Date range requested: ${startDate} - ${endDate}` : '',
      request.specificRecords ? `Specific records: ${request.specificRecords}` : '',
      request.notes ? `Additional notes: ${request.notes}` : '',
      destinationSummary ? `Deliver records directly to: ${destinationSummary}` : '',
      uploadLink ? `Secure upload link: ${uploadLink}` : '',
      uploadLink ? 'Please use this link to securely upload requested records to MedCaddy.' : '',
      '',
      'Thank you,',
      'MedCaddy'
    ].filter(Boolean);

    return {
      to: request.email || '',
      subject: `Medical Records Request - ${requestLabel}`,
      message: messageLines.join('\n')
    };
  }, [portalLink, recordTypeLabels, request]);

  // Effects

  useEffect(() => {
    setTestEmail(request.email || '');
    setTestSendError('');
    setTestSendSuccess('');
    setShowDevTestSend(false);
    setPortalLink(request.authorization?.shareUrl || '');
    setPortalPin('');
    setPortalError('');
    setPortalCopied('');
    setShowSendPreview(false);
  }, [request.id, request.email, request.authorization?.shareUrl]);

  useEffect(() => {
    const draft = buildEmailDraft();
    setEmailTo(draft.to);
    setEmailSubject(draft.subject);
    setEmailMessage(draft.message);
  }, [buildEmailDraft]);

  useEffect(() => {
    if (request.authorizationId) {
      sessionStorage.removeItem(flowStorageKey);
      setAuthFlowAuthorizationId(null);
      setAuthFlowSigned(false);
      setAuthFlowStatus('idle');
      setAuthFlowError('');
      setAuthFlowStep(1);
      return;
    }
    const saved = sessionStorage.getItem(flowStorageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setAuthFlowAuthorizationId(parsed.authorizationId || null);
        setAuthFlowSigned(Boolean(parsed.signed));
        setAuthFlowStatus(parsed.status || 'idle');
        setAuthFlowStep(parsed.step || 1);
        setAuthFlowError(parsed.error || '');
      } catch {
        sessionStorage.removeItem(flowStorageKey);
      }
    }
  }, [request.authorizationId, flowStorageKey]);

  useEffect(() => {
    const flag = sessionStorage.getItem('medcaddy:provider-request-just-created');
    if (flag === request.id) {
      setJustCreated(true);
      sessionStorage.removeItem('medcaddy:provider-request-just-created');
      const timeout = setTimeout(() => setJustCreated(false), 12000);
      return () => clearTimeout(timeout);
    }
    setJustCreated(false);
  }, [request.id]);

  /** Purpose: Executes persist auth flow. Inputs: next. Output: Operation result. */
  const persistAuthFlow = (next) => {
    sessionStorage.setItem(flowStorageKey, JSON.stringify(next));
  };

  /** Purpose: Updates auth flow. Inputs: next. Output: Operation result. */
  const updateAuthFlow = (next) => {
    const payload = {
      authorizationId: authFlowAuthorizationId,
      signed: authFlowSigned,
      status: authFlowStatus,
      step: authFlowStep,
      error: authFlowError,
      ...next
    };
    setAuthFlowAuthorizationId(payload.authorizationId);
    setAuthFlowSigned(payload.signed);
    setAuthFlowStatus(payload.status);
    setAuthFlowStep(payload.step);
    setAuthFlowError(payload.error);
    persistAuthFlow(payload);
  };

  /** Purpose: Executes clear auth flow. Inputs: none. Output: Operation result. */
  const clearAuthFlow = () => {
    sessionStorage.removeItem(flowStorageKey);
    setAuthFlowAuthorizationId(null);
    setAuthFlowSigned(false);
    setAuthFlowStatus('idle');
    setAuthFlowError('');
    setAuthFlowStep(1);
  };

  /** Purpose: Executes refresh request. Inputs: none. Output: Promise with operation result. */
  const refreshRequest = async () => {
    try {
      const response = await apiFetch(`/provider-requests/${request.id}`);
      if (response.ok) {
        const updated = await response.json();
        onUpdated(updated);
      } else if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Refresh error:', err);
    }
  };

  /** Purpose: Creates authorization. Inputs: none. Output: Promise with operation result. */
  const createAuthorization = async () => {
    const destinationLabel = [request.destinationProviderName, request.destinationOrganization]
      .filter(Boolean)
      .join(' at ');
    const destinationInstructions = destinationLabel
      ? `Release records directly to ${destinationLabel}.`
      : '';
    const additionalInstructions = [request.specificRecords, destinationInstructions]
      .filter(Boolean)
      .join(' ');
    const purposeLine = destinationLabel
      ? `Authorize release of medical records from ${request.organization || request.providerName} to ${destinationLabel}`
      : `Release of medical records as requested: ${request.recordTypes?.join(', ')}`;

    const response = await apiFetch('/authorizations', {
      method: 'POST',
      body: JSON.stringify({
        disclosingEntityName,
        disclosingEntityOrganization,
        disclosingEntityAddress,
        disclosingEntityPhone,
        disclosingEntityFax,
        revocationContact,
        grantedTo: request.providerName,
        grantedToTitle: request.providerTitle || '',
        organization: request.organization,
        organizationAddress: [request.address, request.city, request.state, request.postalCode].filter(Boolean).join(', '),
        organizationPhone: request.phone || '',
        organizationFax: request.fax || '',
        providerNpi: request.providerNpi || '',
        npiVerified: request.npiVerified || false,
        purposeCategory: 'TREATMENT',
        purpose: purposeLine,
        purposeDetails: additionalInstructions,
        phiDescription: request.recordTypes?.join(', ') || 'Medical records',
        sensitiveCategories: [],
        expiryDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        accessLevel: 'READ',
        recordIds: []
      })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to create authorization');
    }

    const authorization = await response.json();
    return authorization.id;
  };

  /** Purpose: Executes sign authorization. Inputs: authorizationId. Output: Promise with operation result. */
  const signAuthorization = async (authorizationId) => {
    if (!signatureData) {
      throw new Error('Signature required to continue');
    }
    const signResponse = await apiFetch(`/authorizations/${authorizationId}/sign`, {
      method: 'POST',
      body: JSON.stringify({ signatureData })
    });

    if (!signResponse.ok) {
      throw new Error('Failed to sign authorization');
    }
  };

  /** Purpose: Executes attach authorization. Inputs: authorizationId. Output: Promise with operation result. */
  const attachAuthorization = async (authorizationId) => {
    const attachResponse = await apiFetch(`/provider-requests/${request.id}/attach-authorization`, {
      method: 'POST',
      body: JSON.stringify({ authorizationId })
    });

    if (!attachResponse.ok) {
      throw new Error('Failed to attach authorization');
    }

    const updated = await attachResponse.json();
    onUpdated(updated);
    await refreshRequest();
  };

  // Create authorization and attach to request
  /** Purpose: Handles create and attach authorization. Inputs: none. Output: Promise with operation result. */
  const handleCreateAndAttachAuthorization = async () => {
    if (!signatureData) {
      setAuthFlowError('Please sign the authorization');
      return;
    }

    setIsUpdating(true);
    updateAuthFlow({ status: 'running', step: 1, error: '' });
    try {
      const authorizationId = await createAuthorization();
      updateAuthFlow({ authorizationId, step: 2 });
      await signAuthorization(authorizationId);
      updateAuthFlow({ signed: true, step: 3 });
      await attachAuthorization(authorizationId);

      clearAuthFlow();
      setShowCreateAuth(false);
      setAuthStep(1);
      setSignatureData(null);
    } catch (err) {
      console.error('Authorization error:', err);
      updateAuthFlow({ status: 'error', error: err.message || 'Failed to create authorization' });
    } finally {
      setIsUpdating(false);
    }
  };

  /** Purpose: Handles resume authorization. Inputs: none. Output: Promise with operation result. */
  const handleResumeAuthorization = async () => {
    if (!authFlowAuthorizationId) return;
    setIsUpdating(true);
    updateAuthFlow({ status: 'running', error: '' });
    try {
      if (!authFlowSigned) {
        await signAuthorization(authFlowAuthorizationId);
        updateAuthFlow({ signed: true, step: 3 });
      }
      await attachAuthorization(authFlowAuthorizationId);
      clearAuthFlow();
      setShowCreateAuth(false);
      setAuthStep(1);
      setSignatureData(null);
    } catch (err) {
      console.error('Resume authorization error:', err);
      updateAuthFlow({ status: 'error', error: err.message || 'Failed to resume authorization' });
      if (err.message && err.message.includes('Signature')) {
        setShowCreateAuth(true);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  /** Purpose: Handles restart authorization. Inputs: none. Output: Operation result. */
  const handleRestartAuthorization = () => {
    clearAuthFlow();
    setSignatureData(null);
    setAuthStep(1);
    setShowCreateAuth(true);
  };

  /** Purpose: Handles attach existing authorization. Inputs: authId. Output: Promise with operation result. */
  const handleAttachExistingAuthorization = async (authId) => {
    setIsUpdating(true);
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/attach-authorization`, {
        method: 'POST',
        body: JSON.stringify({ authorizationId: authId })
      });

      if (response.ok) {
        const updated = await response.json();
        onUpdated(updated);
        await refreshRequest();
      }
    } catch (err) {
      console.error('Attach error:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  /** Purpose: Sends request email with editable draft fields. Inputs: none. Output: Promise with operation result. */
  const handleSend = async () => {
    if (!hasPortalLink) {
      setSendError('Generate the portal upload link before sending email.');
      return;
    }

    const recipient = emailTo.trim();
    if (!recipient) {
      setSendError('Recipient email is required.');
      return;
    }

    setIsUpdating(true);
    setSendError('');
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/send`, {
        method: 'POST',
        body: JSON.stringify({
          method: request.deliveryMethod,
          to: recipient,
          subject: emailSubject.trim(),
          message: emailMessage.trim()
        })
      });

      if (response.ok) {
        const updated = await response.json();
        onUpdated(updated);
        await refreshRequest();
        setShowSendPreview(false);
        toast.success('Email sent successfully');
      } else {
        const data = await response.json();
        setSendError(data.error || 'Failed to send request');
      }
    } catch (err) {
      console.error('Send error:', err);
      setSendError('Failed to send request');
    } finally {
      setIsUpdating(false);
    }
  };

  /** Purpose: Creates a Gmail draft in the patient's connected inbox. Inputs: none. Output: Promise with operation result. */
  const handleCreateGmailDraft = async () => {
    if (!hasPortalLink) {
      setGmailDraftError('Generate the portal upload link before creating a Gmail draft.');
      return;
    }

    const recipient = emailTo.trim() || (request.email || '').trim();
    if (!recipient) {
      setGmailDraftError('Recipient email is required.');
      return;
    }

    setGmailDraftLoading(true);
    setGmailDraftError('');
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/gmail-draft`, {
        method: 'POST',
        body: JSON.stringify({
          to: recipient,
          subject: emailSubject.trim(),
          message: emailMessage.trim()
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setGmailDraftError(data.error || data.message || 'Failed to create Gmail draft');
        return;
      }

      const data = await response.json();
      if (data?.draftUrl) {
        window.open(data.draftUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Gmail draft error:', err);
      setGmailDraftError('Failed to create Gmail draft');
    } finally {
      setGmailDraftLoading(false);
    }
  };

  /** Purpose: Sends the request through connected Gmail without leaving the app. Inputs: none. Output: Promise with operation result. */
  const handleSendViaGmail = async () => {
    if (!hasPortalLink) {
      setGmailDraftError('Generate the portal upload link before sending email.');
      return;
    }

    const recipient = emailTo.trim() || (request.email || '').trim();
    if (!recipient) {
      setGmailDraftError('Recipient email is required.');
      return;
    }

    setIsUpdating(true);
    setSendError('');
    setGmailDraftError('');
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/gmail-send`, {
        method: 'POST',
        body: JSON.stringify({
          to: recipient,
          subject: emailSubject.trim(),
          message: emailMessage.trim()
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setGmailDraftError(data.error || data.message || 'Failed to send via connected Gmail');
        return;
      }

      const updated = await response.json();
      onUpdated(updated);
      await refreshRequest();
      setShowSendPreview(false);
      toast.success('Email sent successfully');
    } catch (err) {
      console.error('Gmail send error:', err);
      setGmailDraftError('Failed to send via connected Gmail');
    } finally {
      setIsUpdating(false);
    }
  };

  /** Purpose: Marks request sent without app-driven email transmission. Inputs: none. Output: Promise with operation result. */
  const handleConfirmSentManual = async () => {
    setIsUpdating(true);
    setSendError('');
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/send`, {
        method: 'POST',
        body: JSON.stringify({ method: request.deliveryMethod })
      });

      if (response.ok) {
        const updated = await response.json();
        onUpdated(updated);
        await refreshRequest();
        setShowSendPreview(false);
      } else {
        const data = await response.json();
        setSendError(data.error || 'Failed to mark request as sent');
      }
    } catch (err) {
      console.error('Manual send confirm error:', err);
      setSendError('Failed to mark request as sent');
    } finally {
      setIsUpdating(false);
    }
  };

  /** Purpose: Handles test send. Inputs: none. Output: Promise with operation result. */
  const handleTestSend = async () => {
    setTestSendError('');
    setTestSendSuccess('');
    const targetEmail = testEmail.trim() || request.email || '';
    if (!targetEmail) {
      setTestSendError('Enter a test recipient email.');
      return;
    }
    setTestSending(true);
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/send-test`, {
        method: 'POST',
        body: JSON.stringify({ to: targetEmail })
      });
      if (!response.ok) {
        const data = await response.json();
        setTestSendError(data.error || 'Failed to send test email.');
      } else {
        setTestSendSuccess(`Test email sent to ${targetEmail}.`);
      }
    } catch (err) {
      console.error('Test send error:', err);
      setTestSendError('Failed to send test email.');
    } finally {
      setTestSending(false);
    }
  };

  /** Purpose: Handles status update. Inputs: status, notes = ''. Output: Promise with operation result. */
  const handleStatusUpdate = async (status, notes = '') => {
    if (status === 'DENIED' && !notes) {
      setStatusError('Please provide a denial reason.');
      return;
    }
    setStatusError('');
    setIsUpdating(true);
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status, notes })
      });

      if (response.ok) {
        const updated = await response.json();
        onUpdated(updated);
        setShowStatusUpdate(false);
        setDenialReason('');
        await refreshRequest();
      }
    } catch (err) {
      console.error('Status update error:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  /** Purpose: Handles add note. Inputs: none. Output: Promise with operation result. */
  const handleAddNote = async () => {
    if (!noteText.trim()) {
      setNoteError('Add a note before saving.');
      return;
    }
    setNoteSaving(true);
    setNoteError('');
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/communications`, {
        method: 'POST',
        body: JSON.stringify({
          type: 'note',
          direction: 'outbound',
          content: noteText.trim()
        })
      });
      if (!response.ok) {
        throw new Error('Failed to save note');
      }
      setNoteText('');
      await refreshRequest();
    } catch (err) {
      console.error('Note error:', err);
      setNoteError('Failed to save note.');
    } finally {
      setNoteSaving(false);
    }
  };

  /** Purpose: Handles log follow up. Inputs: none. Output: Promise with operation result. */
  const handleLogFollowUp = async () => {
    setNoteSaving(true);
    setNoteError('');
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/communications`, {
        method: 'POST',
        body: JSON.stringify({
          type: 'note',
          direction: 'outbound',
          content: `Follow-up reminder sent (${new Date().toLocaleDateString()})`
        })
      });
      if (!response.ok) {
        throw new Error('Failed to log follow-up');
      }
      await refreshRequest();
    } catch (err) {
      console.error('Follow-up note error:', err);
      setNoteError('Failed to log follow-up reminder.');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleReopen = async () => {
    setReopenLoading(true);
    setReopenError('');
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/reopen`, {
        method: 'POST',
        body: JSON.stringify({
          message: reopenMessage.trim() || undefined,
          sendFollowUp: reopenSendFollowUp
        })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to reopen request');
      }
      const updated = await response.json();
      onUpdated(updated);
      setShowReopenPanel(false);
      setReopenMessage('');
      setReopenSendFollowUp(true);
      await refreshRequest();
    } catch (err) {
      console.error('Reopen error:', err);
      setReopenError(err.message || 'Failed to reopen request.');
    } finally {
      setReopenLoading(false);
    }
  };

  /** Purpose: Executes generate portal pin. Inputs: none. Output: Operation result. */
  const generatePortalPin = () => {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return String(100000 + (array[0] % 900000));
  };

  /** Purpose: Handles generate portal link. Inputs: none. Output: Promise with operation result. */
  const handleGeneratePortalLink = async () => {
    if (!resolvedAuthorizationId) {
      setPortalError('Attach a signed authorization before generating a portal link.');
      return;
    }
    setPortalLoading(true);
    setPortalError('');
    setPortalCopied('');
    try {
      const pin = generatePortalPin();
      const response = await apiFetch(`/share/generate/${resolvedAuthorizationId}`, {
        method: 'POST',
        body: JSON.stringify({ pin })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (data.code === 'EMAIL_NOT_VERIFIED' || data.error === 'EMAIL_NOT_VERIFIED') {
          throw new Error('Please verify your account email before generating share links.');
        }
        if (response.status === 401) {
          throw new Error('Session expired. Refresh and sign in again.');
        }
        throw new Error(data.error || data.message || 'Failed to generate portal link');
      }
      const data = await response.json();
      setPortalLink(data.shareUrl);
      setPortalPin(pin);
      setSendError('');
      setGmailDraftError('');
    } catch (err) {
      console.error('Portal link error:', err);
      setPortalError(err.message || 'Failed to generate portal link');
    } finally {
      setPortalLoading(false);
    }
  };

  /** Purpose: Handles copy. Inputs: label, value. Output: Promise with operation result. */
  const handleCopy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setPortalCopied(`${label} copied`);
      setTimeout(() => setPortalCopied(''), 2000);
    } catch (err) {
      setPortalError('Failed to copy to clipboard');
    }
  };

  /** Purpose: Handles download package. Inputs: none. Output: Promise with operation result. */
  const handleDownloadPackage = async () => {
    try {
      const response = await apiFetch(`/provider-requests/${request.id}/package`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Records_Request_${request.organization.replace(/\s+/g, '_')}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  /** Purpose: Handles delete request. Inputs: none. Output: Promise with operation result. */
  const handleDeleteRequest = async () => {
    if (!window.confirm('Delete this provider request? This cannot be undone.')) return;
    setIsUpdating(true);
    try {
      const response = await apiFetch(`/provider-requests/${request.id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        throw new Error('Failed to delete request');
      }
      if (onRefresh) {
        onRefresh();
      }
      if (typeof onDeleted === 'function') {
        onDeleted(request.id);
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete request');
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    // State values
    isUpdating,
    showStatusUpdate,
    showCreateAuth,
    authStep,
    signatureData,
    showSignaturePad,
    justCreated,
    authFlowStatus,
    authFlowStep,
    authFlowError,
    authFlowAuthorizationId,
    authFlowSigned,
    statusError,
    denialReason,
    sendError,
    gmailDraftError,
    gmailDraftLoading,
    showSendPreview,
    emailTo,
    emailSubject,
    emailMessage,
    testEmail,
    testSending,
    testSendError,
    testSendSuccess,
    noteText,
    noteError,
    noteSaving,
    portalLink,
    portalPin,
    portalError,
    portalLoading,
    portalCopied,
    showDevTestSend,
    showReopenPanel,
    reopenMessage,
    reopenSendFollowUp,
    reopenLoading,
    reopenError,

    // Setters
    setShowStatusUpdate,
    setShowCreateAuth,
    setAuthStep,
    setSignatureData,
    setShowSignaturePad,
    setStatusError,
    setDenialReason,
    setSendError,
    setGmailDraftError,
    setShowSendPreview,
    setEmailTo,
    setEmailSubject,
    setEmailMessage,
    setTestEmail,
    setTestSendError,
    setTestSendSuccess,
    setShowDevTestSend,
    setShowReopenPanel,
    setReopenMessage,
    setReopenSendFollowUp,
    setNoteText,
    setNoteError,

    // Derived values
    resolvedAuthorizationId,
    disclosingEntityName,
    disclosingEntityOrganization,
    disclosingEntityAddress,
    disclosingEntityPhone,
    disclosingEntityFax,
    revocationContact,
    statusConfig,
    recordTypeLabels,
    canComposeEmail,
    isManualDelivery,
    hasPortalLink,
    isDev,
    sentAtDate,
    daysSinceSent,
    needsFollowUp,
    terminalStatus,
    receivedLike,
    timelineStages,
    currentTimelineIndex,
    nextAction,
    activeAuthorizations,

    // Handlers
    buildEmailDraft,
    refreshRequest,
    createAuthorization,
    signAuthorization,
    attachAuthorization,
    handleCreateAndAttachAuthorization,
    handleResumeAuthorization,
    handleRestartAuthorization,
    handleAttachExistingAuthorization,
    handleSend,
    handleCreateGmailDraft,
    handleSendViaGmail,
    handleConfirmSentManual,
    handleTestSend,
    handleStatusUpdate,
    handleAddNote,
    handleLogFollowUp,
    handleReopen,
    generatePortalPin,
    handleGeneratePortalLink,
    handleCopy,
    handleDownloadPackage,
    handleDeleteRequest,
    persistAuthFlow,
    updateAuthFlow,
    clearAuthFlow,
  };
}
